import type { AriaSelectProps } from '@react-types/select';
import type { KeyboardEvent } from '@react-types/shared';
import type { PopoverProps } from 'react-aria-components';
import { mergeRefs } from '@react-aria/utils';
import { merge } from 'lodash-es';
import * as React from 'react';
import { HiddenSelect, useSelect } from 'react-aria';
import { Popover } from 'react-aria-components';
import { useSelectState } from 'react-stately';
import useMeasure from 'react-use-measure';

import type { FromSingleSelectionProps } from '../../common/props';
import type {
  ControlAppearance,
  ControlMaxWidth,
  ControlMinWidth,
  ControlSize,
  ControlVariant,
  ControlWidth,
} from '../../controls/shared/types';
import { type IconName, Icon } from '../../assets/Icon/Icon';
import { contentStyles } from '../../common/menus';
import { toSingleSelectionProps } from '../../common/props';
import { zIndexes } from '../../common/z_indexes';
import { Button } from '../../controls/Button/Button';
import { ControlGroup } from '../../controls/ControlGroup/ControlGroup';
import {
  useControlAppearance,
  useControlGhost,
  useControlMaxWidth,
  useControlMinWidth,
  useControlSize,
  useControlVariant,
  useControlWidth,
} from '../../controls/shared/context';
import { iconColor, labelColor, transitions } from '../../controls/shared/styles';
import { colors, styled } from '../../stitches.config';
import { BodySansSizes } from '../../text/Body';
import { LargeSansSizes } from '../../text/Large';
import { SmallSansSizes } from '../../text/Small';
import { Text } from '../../text/Text';
import { maxWidthCSS } from '../../utilities/shared/MaxWidth';
import { minWidthCSS } from '../../utilities/shared/MinWidth';
import { widthCSS } from '../../utilities/shared/Width';
import { ComboBoxList } from '../ComboBox/ComboBoxList';
import { SelectContainer, SelectTrigger } from './SelectTrigger';

type SelectProps<T> = FromSingleSelectionProps<AriaSelectProps<T>> & {
  appearance?: ControlAppearance;
  canClearValue?: boolean;
  size?: ControlSize;
  disabled?: boolean;
  ghost?: boolean;
  icon?: IconName;
  indeterminate?: boolean;
  invalid?: boolean;
  placeholder?: string;
  popoverProps?: PopoverProps;
  showPlaceholderForIndeterminate?: boolean;
  variant?: ControlVariant;
  width?: ControlWidth;
  maxWidth?: ControlMaxWidth;
  minWidth?: ControlMinWidth;
};

const SelectIcon = styled(Icon, {
  alignSelf: 'center',
  width: '$$iconSize',
  height: '$$iconSize',
  color: iconColor,

  variants: {
    ghost: {
      true: {
        color: colors.transparent,
      },
      false: {
        color: iconColor,
      },
    },
  },
});
SelectIcon.displayName = 'SelectIcon';

export const SelectValue = styled(Text, {
  width: '100%',
  color: labelColor,
  textAlign: 'left',
  truncate: true,
  transition: transitions.control,

  variants: {
    size: {
      'x-small': {
        ...SmallSansSizes,
      },
      small: {
        ...SmallSansSizes,
      },
      medium: {
        ...BodySansSizes,
      },
      large: {
        ...BodySansSizes,
      },
      'x-large': {
        ...LargeSansSizes,
      },
    },
  },
});
SelectValue.displayName = 'SelectValue';

const SelectPopover = styled(Popover, contentStyles, {
  padding: '$4',
  overflow: 'auto',
});
SelectPopover.displayName = 'SelectPopover';

function SelectInner<T extends object>(props: SelectProps<T>, ref: React.Ref<HTMLButtonElement>) {
  const renamedProps = toSingleSelectionProps(props);
  const {
    placeholder = 'Select...',
    canClearValue,
    indeterminate,
    showPlaceholderForIndeterminate,
  } = renamedProps;

  const controlAppearance = useControlAppearance(props.appearance, 'muted');
  const controlGhost = useControlGhost(props.ghost, false);
  const controlSize = useControlSize(props.size, 'medium');
  const controlVariant = useControlVariant(props.variant, 'secondary');
  const controlWidth = useControlWidth(props.width) ?? 'min-content';
  const controlMaxWidth = useControlMaxWidth(props.maxWidth) ?? '100%';
  const controlMinWidth = useControlMinWidth(props.minWidth) ?? 0;

  const css = merge(
    widthCSS(controlWidth),
    maxWidthCSS(controlMaxWidth),
    minWidthCSS(controlMinWidth),
  );

  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const state = useSelectState(renamedProps);

  const [measureRef, { width: inputWidth }] = useMeasure();

  const { triggerProps, valueProps, menuProps } = useSelect(renamedProps, state, triggerRef);

  const onKeyDown = React.useCallback(
    (e: KeyboardEvent) => {
      if (triggerProps.onKeyDown) {
        triggerProps.onKeyDown(e);
      }
      if (props.onKeyDown) {
        props.onKeyDown(e);
      }
    },
    [props, triggerProps],
  );

  const selectTrigger = (
    <SelectTrigger
      {...triggerProps}
      onKeyDown={onKeyDown}
      ref={mergeRefs(ref, triggerRef, measureRef as unknown as React.Ref<HTMLButtonElement>)}
      size={controlSize}
      appearance={controlGhost ? 'muted' : controlAppearance}
      variant={controlVariant}
      ghost={controlGhost}
      data-ghost={controlGhost}
      invalid={props.invalid}
      isDisabled={props.disabled ?? false}
      css={css}
    >
      {props.icon && <SelectIcon icon={props.icon} />}
      <SelectValue {...valueProps} size={controlSize}>
        {/* eslint-disable-next-line no-nested-ternary */}
        {indeterminate ? (
          showPlaceholderForIndeterminate ? (
            placeholder
          ) : (
            <>&ndash;&nbsp;</>
          )
        ) : state.selectedItem ? (
          state.selectedItem.rendered
        ) : (
          placeholder
        )}
      </SelectValue>
      <SelectIcon ghost={props.ghost} icon="chevrons-vertical" />
    </SelectTrigger>
  );

  return (
    <SelectContainer css={css}>
      <HiddenSelect state={state} triggerRef={triggerRef} label={props.label} name={props.name} />
      {state.selectedItem && canClearValue ? (
        <ControlGroup size={controlSize}>
          {selectTrigger}
          <Button
            type="button"
            icon="cross"
            variant="secondary"
            arrangement="hidden-label"
            condense
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              state.setSelectedKey('');
            }}
          >
            Clear selection
          </Button>
        </ControlGroup>
      ) : (
        selectTrigger
      )}
      <SelectPopover
        triggerRef={triggerRef}
        isOpen={state.isOpen}
        onOpenChange={state.setOpen}
        placement="bottom start"
        {...props.popoverProps}
        style={{
          minWidth: inputWidth,
          // 400px is an arbitrary width that prevents unusually wide menus.
          // Generally we want the menu to be only as wide as necessary to fit the content,
          // but beyond 400px text should wrap to a new line.
          maxWidth: Math.max(inputWidth, 400),
          zIndex: zIndexes.overlay,
        }}
      >
        <ComboBoxList {...menuProps} state={state} />
      </SelectPopover>
    </SelectContainer>
  );
}

export const Select = React.forwardRef(SelectInner) as <T extends object>(
  props: SelectProps<T> & { ref?: React.ForwardedRef<HTMLButtonElement> },
) => ReturnType<typeof SelectInner>;

export { Item as SelectItem, Section as SelectSection } from 'react-stately';
export type { SelectProps };
